import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import FormSection from '@/components/Forms/FormSection';
import { SystemEfficienciesSection } from '@/components/InstallMilestonePackage/SystemEfficienciesSection';
import { FlagBox } from '@/components/QueueFlags/FlagBox';
import { intercomTarget } from '@/helpers/intercomTarget';
import usePermissions from '@/hooks/usePermissions';
import { useGetDocumentsQuery, useUploadDocumentMutation } from '@/services/documents';
import { useGetHvacEquipmentCatalogQuery } from '@/services/equipmentCatalog';
import { isErrorWithData } from '@/services/helpers';
import {
  Accordion,
  Alert,
  Badge,
  Box,
  Button,
  FormikSelectInputNative,
  FormikTextInput,
  SelectInputNative,
  SimulatedEventPayloadType,
  TextInput,
  toast,
  useBreakpoint,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import {
  DocumentDocument,
  HVACAccessoryType,
  HVACEquipmentType,
  HVACInstallPackageFileCategory,
  HVACManufacturer,
  HVACSystem,
  HVACSystemCategory,
  HVACSystemCategoryDefaultEquipmentTypes,
  isMiniSplitSystemCategory,
  RequirementFlagSettingsType,
  UserPermissions,
} from 'types';

import { AccessoriesSection } from './AccessoriesSection';
import { DiscountsSection } from './DiscountsSection';
import { EquipmentAndMaterialsSection } from './EquipmentAndMaterialsSection';
import { InstallPackageAccessoriesSection } from './InstallPackageAccessoriesSection';
import { InstallPackageEquipmentSection } from './InstallPackageEquipmentSection';
import { SitePrepSection } from './SitePrepSection';

// Custom type for form state - allows empty string for systemCategory (unselected state)
// and optional conditionedArea since these fields start empty in the form
type HVACSystemItemForm = Omit<HVACSystem, 'systemCategory' | 'conditionedArea'> & {
  systemCategory: string;
  conditionedArea?: number;
};

type UploadButtonProps = {
  category: string;
  internalReferences: { fieldname: string; value: string }[];
  isPackageEditable?: boolean;
};

const handleStopPropagation = (event: any) => event.stopPropagation();

const UploadButton = ({ category, internalReferences, isPackageEditable = true }: UploadButtonProps) => {
  const { id = '' } = useParams<{ id: string }>();
  const { isPhone } = useBreakpoint();
  const [uploadDocument, { isLoading: isUploadingDocument }] = useUploadDocumentMutation();

  const handleFileUpload = useCallback(
    async (event: any) => {
      const files = event.target?.files;
      const title = event.target?.name;

      if (files) {
        const data = new FormData();
        data.append('type', HVACInstallPackageFileCategory.ahriCertificate);
        data.append('internalReferences', JSON.stringify(internalReferences));

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          data.append(`files[${i}]`, file, file.name);
          data.append(`context[${i}]`, title ?? '');
        }

        try {
          await uploadDocument({ accountId: id, formData: data, invalidateAllTags: false }).unwrap();
          toast.success(`${title} photos uploaded`);
        } catch (e: any) {
          if (e.status === 413 || e.originalStatus === 413) {
            toast.error('File size too large. Please upload files smaller than 125MB.');
          } else if (isErrorWithData(e)) {
            toast.error(e.data.message);
          } else {
            toast.error('An error occurred while uploading photos');
          }
        }
      }
    },
    [id, uploadDocument, internalReferences],
  );

  return (
    <ResettingFileUpload
      labelText="Upload"
      id={`upload-${category}-photos`}
      name={`${category}-files`}
      size="sm"
      onChange={handleFileUpload}
      multiple
      buttonText={!isPhone ? 'Upload' : null}
      onClick={handleStopPropagation}
      isDisabled={!isPackageEditable || isUploadingDocument}
    />
  );
};

const systemCategoryOptions = Object.keys(HVACSystemCategory)
  .toSorted()
  .map((key) => ({
    label: key,
    value: key,
  }));

const manufacturerOptions = Object.values(HVACManufacturer)
  .toSorted()
  .map((manufacturer) => ({
    label: manufacturer,
    value: manufacturer,
  }));

// Preliminary mode renders a fixed catalog of accessory rows, so "empty" means every row reset
// to its default state rather than an empty array, which is what final mode uses instead.
// A brand-new system leaves selected/notSelected unset so the user must make an explicit Yes/No
// choice; the mini-split reset instead writes an explicit No, since a mini-split never carries
// these accessories and the checkboxes needed to answer Yes/No are disabled for that category.
const buildEmptyAccessoryItems = (isPreliminary: boolean, { defaultToNo = false } = {}) =>
  isPreliminary
    ? (Object.values(HVACAccessoryType)
        .toSorted()
        .map((type) => ({
          quantity: 0,
          type,
          selected: defaultToNo ? false : undefined,
          notSelected: defaultToNo ? true : undefined,
        })) as any)
    : [];

/**
 * The picker's own "not on the AVL" option. It is never a database row, and the server rejects it
 * as an OEM name, because the flat `manufacturer` string feeds the OnPoint warranty payload and a
 * customer-facing legal PDF.
 */
const OFF_AVL_OPTION_LABEL = 'Off-AVL OEM';
const OFF_AVL_OPTION_VALUE = 'offAvl';

/** Marks a stored record the AVL no longer lists, so re-saving it unchanged stays possible. */
const INACTIVE_OPTION_SUFFIX = ' (no longer active)';

interface SystemManufacturerFieldsProps {
  index: number;
  isDisabled: boolean;
}

/**
 * Manufacturer then brand, cascading, because `FormikSelectInputNative` takes a flat
 * `{label, value}[]` and cannot render optgroups. Ids go to the server; the flat `manufacturer`
 * string is mirrored alongside them so the existing required-ness check and every downstream
 * consumer of that string keep working.
 */
const SystemManufacturerFields = ({ index, isDisabled }: SystemManufacturerFieldsProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const { data: catalog } = useGetHvacEquipmentCatalogQuery();
  const manufacturers = catalog?.manufacturers;

  const selection = values?.systems?.[index]?.systemManufacturer;
  const isOffAvl = !!selection?.offAvl;
  const selectedManufacturerId = isOffAvl ? OFF_AVL_OPTION_VALUE : (selection?.manufacturerId ?? '');

  /*
   * The catalog lists active records only, so a package whose stored selection has since been
   * deactivated would have no `<option>` to display and could not be re-saved unchanged — which is
   * exactly the case the server's keep-if-unchanged rule exists to allow. Carry the stored record
   * through as an option instead, labelled so QC can see it is gone.
   */
  const withStoredOption = (options: { label: string; value: string }[], storedId?: string, storedName?: string) =>
    storedId && !options.some((option) => option.value === storedId)
      ? [...options, { label: `${storedName ?? 'Unknown'}${INACTIVE_OPTION_SUFFIX}`, value: storedId }]
      : options;

  const manufacturerOptions = useMemo(
    () => [
      ...withStoredOption(
        (manufacturers ?? []).map((manufacturer) => ({ label: manufacturer.name, value: manufacturer.id })),
        isOffAvl ? undefined : selection?.manufacturerId,
        selection?.manufacturerName,
      ),
      { label: OFF_AVL_OPTION_LABEL, value: OFF_AVL_OPTION_VALUE },
    ],
    [manufacturers, isOffAvl, selection?.manufacturerId, selection?.manufacturerName],
  );

  const brands = useMemo(
    () => manufacturers?.find((manufacturer) => manufacturer.id === selectedManufacturerId)?.brands ?? [],
    [manufacturers, selectedManufacturerId],
  );

  const brandOptions = useMemo(
    () =>
      withStoredOption(
        brands.map((brand) => ({ label: brand.name, value: brand.id })),
        isOffAvl ? undefined : selection?.brandId,
        selection?.brandName,
      ),
    [brands, isOffAvl, selection?.brandId, selection?.brandName],
  );

  const handleManufacturerChange = useCallback(
    (event: any) => {
      const manufacturerId = event.target.value;
      // Changing the manufacturer clears the brand, since brands do not span manufacturers.
      if (manufacturerId === OFF_AVL_OPTION_VALUE) {
        setFieldValue(`systems.${index}.systemManufacturer`, { offAvl: true, offAvlName: '' });
      } else if (manufacturerId) {
        setFieldValue(`systems.${index}.systemManufacturer`, { offAvl: false, manufacturerId });
      } else {
        setFieldValue(`systems.${index}.systemManufacturer`, undefined);
      }
      setFieldValue(`systems.${index}.manufacturer`, '');
    },
    [index, setFieldValue],
  );

  const handleBrandChange = useCallback(
    (event: any) => {
      const brandId = event.target.value;
      const brand = brands.find((candidate) => candidate.id === brandId);
      setFieldValue(`systems.${index}.systemManufacturer`, {
        offAvl: false,
        manufacturerId: selectedManufacturerId,
        ...(brandId ? { brandId } : {}),
      });
      setFieldValue(`systems.${index}.manufacturer`, brand?.name ?? '');
    },
    [brands, index, selectedManufacturerId, setFieldValue],
  );

  const handleOffAvlNameChange = useCallback(
    (event: any) => {
      const offAvlName = event.target.value;
      setFieldValue(`systems.${index}.systemManufacturer`, { offAvl: true, offAvlName });
      setFieldValue(`systems.${index}.manufacturer`, offAvlName);
    },
    [index, setFieldValue],
  );

  return (
    <Box direction={{ base: 'column', desktop: 'row' }} gap="lg">
      <SelectInputNative
        id={`systems.${index}.systemManufacturer.manufacturerId`}
        name={`systems.${index}.systemManufacturer.manufacturerId`}
        label="Manufacturer"
        options={manufacturerOptions}
        value={selectedManufacturerId}
        onChange={handleManufacturerChange}
        isRequired
        isDisabled={isDisabled}
        width="100"
      />
      {isOffAvl ? (
        <TextInput
          id={`systems.${index}.systemManufacturer.offAvlName`}
          name={`systems.${index}.systemManufacturer.offAvlName`}
          label="OEM Name"
          autoComplete="off"
          value={selection?.offAvlName ?? ''}
          onChange={handleOffAvlNameChange}
          isRequired
          isDisabled={isDisabled}
          width="100"
        />
      ) : (
        <SelectInputNative
          id={`systems.${index}.systemManufacturer.brandId`}
          name={`systems.${index}.systemManufacturer.brandId`}
          label="Brand"
          options={brandOptions}
          value={selection?.brandId ?? ''}
          onChange={handleBrandChange}
          isRequired
          isDisabled={isDisabled || !selectedManufacturerId}
          width="100"
        />
      )}
    </Box>
  );
};

interface SystemSectionProps {
  isInstallPackage?: boolean;
  isPreliminary?: boolean;
  isFinCoUser?: boolean;
  action?: string;
  isPackageEditable?: boolean;
  showAhriSection?: boolean;
}

export const SystemSection = ({
  isInstallPackage = false,
  isPreliminary = false,
  isFinCoUser = false,
  action,
  isPackageEditable = true,
  showAhriSection = true,
}: SystemSectionProps) => {
  const { id = '' } = useParams<{ id: string }>();
  const { values, setFieldValue } = useFormikContext<any>();
  const { enableAvlSystemManufacturerSelect } = useFlags();

  const { data: ahriDocuments = [] } = useGetDocumentsQuery(
    { accountId: id, documentTypeFilter: [HVACInstallPackageFileCategory.ahriCertificate] },
    { skip: !isInstallPackage || !id || !showAhriSection },
  );

  const hasDocumentForSystem = (index: number) => {
    return ahriDocuments.some((doc: DocumentDocument) =>
      doc.internalReferences?.some(
        (ref: any) => ref.fieldname === 'systemName' && ref.value === values?.systems?.[index]?.name,
      ),
    );
  };

  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>(() => {
    const initialPanels: Record<string, boolean> = {};
    values?.systems?.forEach((_: any, index: number) => {
      initialPanels[(index + 1).toString()] = true;
    });
    return initialPanels;
  });
  const permissions = usePermissions();
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );

  const systemTitleRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const handleAddSystem = (push: (obj: Omit<HVACSystemItemForm, 'efficiencies'>) => void) => {
    const closeExistingPanels = Object.keys(openPanels).reduce((acc, key) => {
      return { ...acc, [key]: false };
    }, {});
    const newSystemIndex = values.systems.length;
    setOpenPanels({ ...closeExistingPanels, [`${newSystemIndex + 1}`]: true });
    push({
      name: `System ${newSystemIndex + 1}`,
      systemCategory: '',
      equipment: {
        totalCost: 0,
        items: [],
      },
      accessories: {
        totalCost: 0,
        items: buildEmptyAccessoryItems(isPreliminary),
      },
      sitePrep: {
        totalCost: 0,
        items: [],
      },
      discounts: {
        totalCost: 0,
        items: [],
      },
    });

    setTimeout(() => {
      const newSystemTitleRef = systemTitleRefs.current[newSystemIndex];
      if (newSystemTitleRef) {
        newSystemTitleRef.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  };

  useEffect(() => {
    if (values?.systems?.length && isInstallPackage) {
      const initialPanels: Record<string, boolean> = {};
      values.systems.forEach((_: HVACSystem, index: number) => {
        initialPanels[`${index + 1}`] = true;
      });
      setOpenPanels(initialPanels);
    }
  }, [values?.systems, isInstallPackage]);

  return (
    <Box padding={{ base: '0 2xs' }}>
      <FieldArray
        name="systems"
        // oxlint-disable-next-line typescript/unbound-method
        render={({ push, remove }) => {
          return (
            <Accordion>
              <Box>
                {values?.systems?.map((system: HVACSystem, index: number) => {
                  const panelIndex = `${index + 1}`;
                  const systemCategoryOptionsUpdated =
                    Object.keys(HVACSystemCategory).includes(system.systemCategory) ||
                    (system.systemCategory as string) === '' // systemCategory gets set to '' on initial load
                      ? systemCategoryOptions
                      : [
                          ...systemCategoryOptions,
                          { label: `${system.systemCategory} *`, value: system.systemCategory },
                        ];
                  return (
                    <Accordion.Panel isOpen={openPanels[panelIndex]} key={`system-${index}`}>
                      <Accordion.PanelSummary
                        hasCaret="right"
                        onToggle={() => {
                          setOpenPanels({ ...openPanels, [panelIndex]: !openPanels[panelIndex] });
                        }}
                        isDetailsOpen={openPanels[panelIndex]}
                        padding={{ desktop: !openPanels[panelIndex] ? 'xl 0' : 'xl 0 0 0', base: 'xl lg' }}
                      >
                        <Box>
                          <Box direction="row">
                            <Box direction="row" gap="xs" alignItems="center">
                              <Box
                                fontSize="xl"
                                fontWeight="medium"
                                ref={(el: HTMLDivElement | null) => {
                                  systemTitleRefs.current[index] = el;
                                }}
                              >
                                {values?.systems?.[index]?.name}
                              </Box>
                              {values?.systems?.length > 1 ? (
                                <Button
                                  variant="tertiary"
                                  tone="neutral"
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const confirmed = window.confirm('Are you sure you want to remove this system?');
                                    if (!confirmed) return;
                                    remove(index);
                                  }}
                                  iconPrefix="trash"
                                />
                              ) : null}
                            </Box>
                          </Box>
                          {!openPanels[panelIndex] && values?.systems?.[index]?.systemCategory ? (
                            <Box fontSize="md" fontWeight="regular" margin="md 0 0 0">
                              {values?.systems?.[index]?.systemCategory}
                            </Box>
                          ) : null}
                        </Box>
                      </Accordion.PanelSummary>
                      <FormSection baseStyle={{ display: 'block' }}>
                        <Box
                          direction="row"
                          justifyContent="space-between"
                          gap="lg"
                          display="block"
                          id={`system-${index}`}
                        >
                          <Box gap="lg">
                            <Box
                              direction={{ base: 'column', desktop: 'row' }}
                              gap="xl"
                              padding={{ desktop: 'xl 0', base: '0 lg' }}
                              style={{ flexWrap: 'wrap' }}
                            >
                              <Field
                                label="Name"
                                name={`systems.${index}.name`}
                                id={`systems.${index}.name`}
                                component={FormikTextInput}
                                defaultValue={`System ${index + 1}`}
                                isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                width={{ tablet: '4xl' }}
                              />
                              <Field
                                label="System Category"
                                name={`systems.${index}.systemCategory`}
                                id={`systems.${index}.systemCategory`}
                                data-intercom-target={intercomTarget('system-design', 'system-category')}
                                component={FormikSelectInputNative}
                                options={systemCategoryOptionsUpdated}
                                isRequired
                                isDisabled={
                                  userOnlyHasReaderPermission ||
                                  (isInstallPackage && !isFinCoUser) ||
                                  !isPackageEditable
                                }
                                width={{ tablet: '4xl' }}
                                onChange={(event: any) => {
                                  if (isInstallPackage) {
                                    setFieldValue(`systems.${index}.systemCategory`, event.target.value);
                                    return;
                                  }
                                  const targetIsMiniSplit = isMiniSplitSystemCategory(event.target.value);
                                  const hasEquipmentItems = !!values?.systems[index]?.equipment?.items?.length;
                                  const accessoryItems = values?.systems[index]?.accessories?.items ?? [];
                                  const hasAccessoryItems = isPreliminary
                                    ? accessoryItems.some((item: any) => item?.selected === true)
                                    : accessoryItems.length > 0;
                                  const hasExistingCategory = !!values?.systems[index]?.systemCategory;
                                  if (hasExistingCategory && (hasEquipmentItems || hasAccessoryItems)) {
                                    const confirmed = window.confirm(
                                      targetIsMiniSplit
                                        ? 'Changing the System Category to a mini-split will reset the equipment & materials and remove all accessories, which mini-splits do not carry. Do you want to continue?'
                                        : 'Changing the System Category will reset the equipment & materials to match the new System Category. Do you want to continue?',
                                    );
                                    if (!confirmed) {
                                      return;
                                    }
                                  }
                                  setFieldValue(`systems.${index}.systemCategory`, event.target.value);

                                  const defaultEquipmentTypes =
                                    HVACSystemCategoryDefaultEquipmentTypes[
                                      event.target.value as HVACSystemCategory
                                    ];

                                  const equipmentItems = defaultEquipmentTypes.map((item) => ({
                                    type: item,
                                    isRequiredEquipment: item !== HVACEquipmentType.heatStrip,
                                    quantity: 1,
                                  }));
                                  setFieldValue(`systems.${index}.equipment.items`, equipmentItems);
                                  setFieldValue(`systems.${index}['equipment'].equipmentType`, null);

                                  if (targetIsMiniSplit) {
                                    setFieldValue(
                                      `systems.${index}.accessories.items`,
                                      buildEmptyAccessoryItems(isPreliminary, { defaultToNo: true }),
                                    );
                                    setFieldValue(`systems.${index}.accessories.totalCost`, 0);
                                  }
                                }}
                              />
                            </Box>
                            {system.systemCategory === HVACSystemCategory['Furnace Only'] && (
                              <Alert
                                variant="warning"
                                message="This System Configuration is only for homes that do not and will not have air conditioning for the term of the lease."
                                hasIcon
                              />
                            )}
                            {!isInstallPackage ? (
                              <EquipmentAndMaterialsSection
                                systemIndex={index}
                                isPreliminary={isPreliminary}
                                helpLink="https://hep.palmetto.finance/en/articles/12003806-hep-residential-system-categories"
                                isPackageEditable={isPackageEditable}
                                isFinCoUser={isFinCoUser}
                              />
                            ) : (
                              <InstallPackageEquipmentSection
                                systemIndex={index}
                                isPackageEditable={isPackageEditable}
                              />
                            )}
                            {!isInstallPackage ? (
                              <AccessoriesSection
                                systemIndex={index}
                                isPreliminary={isPreliminary}
                                isPackageEditable={isPackageEditable}
                              />
                            ) : (
                              <InstallPackageAccessoriesSection
                                systemIndex={index}
                                isPackageEditable={isPackageEditable}
                              />
                            )}
                            <FormSection title="Equipment & Accessories Total Cost">
                              <Box gap="xl" alignSelf="flex-end">
                                {!isInstallPackage ? (
                                  <Field
                                    name={`systems.${index}.equipment.totalCost`}
                                    id={`systems.${index}.equipment.totalCost`}
                                    component={FormikTextInput}
                                    prefix="$"
                                    width="2xl"
                                    isDisabled={
                                      userOnlyHasReaderPermission ||
                                      //We disable in case we don't have items
                                      !values.systems[index].equipment.items?.length ||
                                      !isPackageEditable
                                    }
                                    onFocus={(event: SimulatedEventPayloadType) => {
                                      if (event?.target?.value === '0') {
                                        setFieldValue(`systems.${index}.equipment.totalCost`, '');
                                      }
                                    }}
                                    onBlur={(event: SimulatedEventPayloadType) => {
                                      const value = parseFloat(event?.target?.value);
                                      setFieldValue(
                                        `systems.${index}.equipment.totalCost`,
                                        isNaN(value) ? 0 : value,
                                      );
                                    }}
                                    onChange={(event: SimulatedEventPayloadType) => {
                                      const value = event?.target?.value;
                                      if (value.endsWith('.')) {
                                        setFieldValue(`systems.${index}.equipment.totalCost`, value);
                                      } else {
                                        const numericValue = Number(value);
                                        if (!isNaN(numericValue)) {
                                          setFieldValue(`systems.${index}.equipment.totalCost`, value);
                                        }
                                      }
                                    }}
                                  />
                                ) : (
                                  <Box fontSize="md" fontWeight="medium">
                                    ${values.systems[index].equipment.totalCost?.toLocaleString()}
                                  </Box>
                                )}
                              </Box>
                            </FormSection>
                            <SitePrepSection
                              systemIndex={index}
                              isInstallPackage={isInstallPackage}
                              isPackageEditable={isPackageEditable}
                            />
                            <DiscountsSection
                              systemIndex={index}
                              isInstallPackage={isInstallPackage}
                              isPackageEditable={isPackageEditable}
                            />
                            {isInstallPackage && showAhriSection ? (
                              <>
                                <FormSection baseStyle={{ borderWidth: '0', direction: 'column' }} title="AHRI">
                                  <FlagBox
                                    dataKey={`AHRI Number ${index + 1}`}
                                    baseCategory="AhriNumber"
                                    flagType={RequirementFlagSettingsType.ahriNumber}
                                    displayWithNoQueue
                                  >
                                    <Box direction="row" gap="lg" alignItems="center">
                                      <Field
                                        type="text"
                                        name={`systems.${index}.ahriNumber`}
                                        id={`systems.${index}.ahriNumber`}
                                        width="2xl"
                                        autoComplete="off"
                                        component={FormikTextInput}
                                        isDisabled={!isPackageEditable}
                                        isRequired
                                      />
                                      <UploadButton
                                        category={`${HVACInstallPackageFileCategory.ahriCertificate}-${index + 1}`}
                                        internalReferences={[
                                          {
                                            fieldname: 'systemName',
                                            value: values?.systems?.[index]?.name,
                                          },
                                        ]}
                                        isPackageEditable={isPackageEditable}
                                      />
                                      {hasDocumentForSystem(index) && (
                                        <Badge
                                          padding="xs"
                                          message="Document Uploaded"
                                          variant="success"
                                          size="sm"
                                        />
                                      )}
                                    </Box>
                                  </FlagBox>
                                </FormSection>
                                {isFinCoUser && (
                                  <SystemEfficienciesSection index={index} isPackageEditable={isPackageEditable} />
                                )}
                                {isFinCoUser && action === 'review' && (
                                  <FormSection
                                    title="System Manufacturer"
                                    baseStyle={{
                                      borderWidth: '0',
                                    }}
                                  >
                                    {enableAvlSystemManufacturerSelect ? (
                                      <SystemManufacturerFields
                                        index={index}
                                        isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                      />
                                    ) : (
                                      <Field
                                        name={`systems.${index}.manufacturer`}
                                        id={`systems.${index}.manufacturer`}
                                        component={FormikSelectInputNative}
                                        options={manufacturerOptions}
                                        isRequired
                                        isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                        width="100"
                                      />
                                    )}
                                  </FormSection>
                                )}
                              </>
                            ) : null}
                          </Box>
                        </Box>
                      </FormSection>
                    </Accordion.Panel>
                  );
                })}
                {!isInstallPackage && (
                  <Button
                    variant="secondary"
                    iconPrefix="add"
                    style={{ alignSelf: 'flex-end', marginTop: '16px' }}
                    onClick={() => handleAddSystem(push)}
                    data-intercom-target={intercomTarget('system-design', 'add-system')}
                  >
                    Add System
                  </Button>
                )}
              </Box>
            </Accordion>
          );
        }}
      />
    </Box>
  );
};
